package com.iregained.site.components.sections.contact.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.contact.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.fetch.NO_CORS
import org.w3c.fetch.RequestInit
import kotlin.js.json

// UNIQUE ANIMATION: Gentle Squeeze
@Composable
private fun Modifier.gentleSqueeze(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 1.02f).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut)
    )
}

@Composable
fun ContactPatientFormSection(data: ContactPatientFormData = defaultContactPatientData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "contact-patient-form-section"
    var isVisible by remember { mutableStateOf(false) }

    val ctx = com.varabyte.kobweb.core.rememberPageContext()
    val intent = ctx.route.params["intent"]
    val initialInquiryType = when (intent) {
        "find_clinic" -> "Find Clinic"; "refer_clinic" -> "General Inquiry"; else -> ""
    }
    var formSubmitted by remember { mutableStateOf(false) }
    var isSubmitting by remember { mutableStateOf(false) }

    if (data.title.isNotBlank()) {
        Column(
            modifier = ContactPatientContainerStyle.toModifier().fillMaxWidth().id(targetId).background(Colors.White)
            .styleModifier { property("scroll-margin-top", "100px") }
            .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent),
            horizontalAlignment = Alignment.CenterHorizontally) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.title.isNotBlank()) {
                        SectionHeader(
                            text = data.title,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s)),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.description.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(900.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).opacity(if (isVisible) 1 else 0).transition(
                                    Transition.of(
                                        "opacity", 0.8.s, delay = 150.ms
                                    )
                                ).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.description; onDispose {}
                                    }
                                })
                    }

                    Box(modifier = PatientFormCardStyle.toModifier().gentleSqueeze(isVisible)) {
                        Form(attrs = Modifier.fillMaxWidth().styleModifier { property("color-scheme", "light") }.toAttrs {
                            attr("action", data.formProcessingEndpoint); attr("method", "POST")
                            onSubmit { e ->
                                e.preventDefault()
                                isSubmitting = true

                                // 1. Grab the honeypot value
                                val honeypot =
                                    (document.getElementById("patient_website_url") as? HTMLInputElement)?.value
                                        ?: ""

                                // 2. Fast-fail
                                if (honeypot.isNotEmpty()) {
                                    isSubmitting = false
                                    formSubmitted = true
                                    return@onSubmit
                                }

                                val fullName =
                                    (document.getElementById("patient_full_name") as? HTMLInputElement)?.value ?: ""
                                val email = (document.getElementById("patient_email") as? HTMLInputElement)?.value ?: ""
                                val location =
                                    (document.getElementById("patient_location") as? HTMLInputElement)?.value ?: ""
                                val inquiryType =
                                    (document.getElementById("patient_inquiry_type") as? HTMLSelectElement)?.value ?: ""
                                val message =
                                    (document.getElementById("patient_message") as? HTMLTextAreaElement)?.value ?: ""
                                val consentGiven =
                                    (document.getElementById("patient_consent") as? HTMLInputElement)?.checked ?: false

                                // 3. Append the honeypot to formData
                                val formData =
                                    "form_id=patient_inquiry" + "&website_url_honeypot=${encodeURIComponent(honeypot)}" + "&full_name=${
                                        encodeURIComponent(fullName)
                                    }" + "&email=${encodeURIComponent(email)}" + "&location=${
                                        encodeURIComponent(
                                            location
                                        )
                                    }" + "&inquiry_type=${encodeURIComponent(inquiryType)}" + "&message=${
                                        encodeURIComponent(
                                            message
                                        )
                                    }" + "&consent=${consentGiven}"

                                val requestInit = RequestInit(
                                    method = "POST",
                                    mode = org.w3c.fetch.RequestMode.NO_CORS,
                                    headers = json("Content-Type" to "application/x-www-form-urlencoded"),
                                    body = formData
                                )
                                window.fetch(data.formProcessingEndpoint, requestInit).then {
                                    isSubmitting = false; formSubmitted = true
                                    (document.getElementById("patient_full_name") as? HTMLInputElement)?.value =
                                        ""; (document.getElementById("patient_email") as? HTMLInputElement)?.value =
                                    ""; (document.getElementById("patient_location") as? HTMLInputElement)?.value =
                                    ""; (document.getElementById("patient_inquiry_type") as? HTMLSelectElement)?.value =
                                    ""; (document.getElementById("patient_message") as? HTMLTextAreaElement)?.value =
                                    ""
                                    (document.getElementById("patient_consent") as? HTMLInputElement)?.checked = false

                                }.catch { isSubmitting = false; window.alert(data.alertErrorMessage) }
                            }
                        }) {
                            // 4. Inject the hidden Honeypot Input
                            Input(
                                type = InputType.Text,
                                attrs = Modifier.opacity(0).position(Position.Absolute).top(0.px).left(0.px).width(1.px)
                                    .height(1.px).styleModifier {
                                        property("pointer-events", "none")
                                    }.toAttrs {
                                        attr("name", "website_url_honeypot")
                                        attr("id", "patient_website_url_honeypot")
                                        attr("tabindex", "-1")
                                        attr("autocomplete", "off")
                                    })

                            Row(modifier = PatientFormRowStyle.toModifier().flexWrap(FlexWrap.Wrap)) {
                                Column(modifier = PatientFormColumnStyle.toModifier()) {
                                    Label(
                                        attrs = PatientFormLabelStyle.toModifier().toAttrs()
                                    ) { Text(data.fullNameLabel) }
                                    Input(
                                        type = InputType.Text, attrs = PatientFormInputStyle.toModifier().toAttrs {
                                            placeholder(data.fullNamePlaceholder); attr(
                                            "name", "full_name"
                                        ); attr("id", "patient_full_name"); attr("required", "true")
                                        })
                                }
                                Column(modifier = PatientFormColumnStyle.toModifier()) {
                                    Label(
                                        attrs = PatientFormLabelStyle.toModifier().toAttrs()
                                    ) { Text(data.emailLabel) }
                                    Input(
                                        type = InputType.Email, attrs = PatientFormInputStyle.toModifier().toAttrs {
                                            placeholder(data.emailPlaceholder); attr(
                                            "name", "email"
                                        ); attr("id", "patient_email"); attr("required", "true")
                                        })
                                }
                            }

                            Row(modifier = PatientFormRowStyle.toModifier().flexWrap(FlexWrap.Wrap)) {
                                Column(modifier = PatientFormColumnStyle.toModifier()) {
                                    Label(
                                        attrs = PatientFormLabelStyle.toModifier().toAttrs()
                                    ) { Text(data.locationLabel) }
                                    Input(
                                        type = InputType.Text, attrs = PatientFormInputStyle.toModifier().toAttrs {
                                            placeholder(data.locationPlaceholder); attr(
                                            "name", "location"
                                        ); attr("id", "patient_location"); attr("required", "true")
                                        })
                                }
                                Column(modifier = PatientFormColumnStyle.toModifier()) {
                                    Label(
                                        attrs = PatientFormLabelStyle.toModifier().toAttrs()
                                    ) { Text(data.inquiryTypeLabel) }
                                    Select(
                                        attrs = PatientFormInputStyle.toModifier().toAttrs {
                                            attr("name", "inquiry_type"); attr(
                                            "id", "patient_inquiry_type"
                                        ); attr("required", "true")
                                        }) {
                                        Option(
                                            value = "",
                                            attrs = { if (initialInquiryType.isEmpty()) selected(); disabled() }) {
                                            Text(
                                                data.inquiryTypePlaceholder
                                            )
                                        }
                                        data.inquiryTypeOptions.forEach { opt ->
                                            Option(
                                                value = opt.value,
                                                attrs = { if (opt.value == initialInquiryType) selected() }) { Text(opt.label) }
                                        }
                                    }
                                }
                            }

                            Column(modifier = PatientFormColumnStyle.toModifier().margin(bottom = 32.px)) {
                                Label(attrs = PatientFormLabelStyle.toModifier().toAttrs()) { Text(data.messageLabel) }
                                TextArea(
                                    attrs = PatientFormInputStyle.toModifier()
                                        .then(PatientFormTextAreaStyle.toModifier()).toAttrs {
                                            placeholder(data.messagePlaceholder); attr("name", "message"); attr(
                                            "id", "patient_message"
                                        ); attr("required", "true")
                                        })
                            }

                            Row(
                                modifier = Modifier.fillMaxWidth().margin(bottom = 32.px),
                                verticalAlignment = Alignment.Top // Ensures checkbox aligns with the first line if text wraps
                            ) {
                                Input(
                                    type = InputType.Checkbox,
                                    attrs = Modifier.size(16.px).margin(top = 4.px, right = 12.px).cursor(Cursor.Pointer).styleModifier {
                                        // Fallback for older browsers to ensure it scales visually
                                        property("transform", "scale(1.2)")
                                        property("transform-origin", "top left")
                                    }.toAttrs {
                                        attr("id", "patient_consent")
                                        attr("name", "consent")
                                        attr("required", "true") // This prevents form submission if unchecked
                                    })
                                Label(
                                    attrs = Modifier.cursor(Cursor.Pointer).lineHeight(1.5).fontSize(14.px)
                                        .color(Color.rgb(0x666666)).toAttrs {
                                            attr("for", "patient_consent")
                                        }) {
                                    Text(data.consentText)
                                }
                            }

                            if (data.buttonText.isNotBlank()) {
                                Button(
                                    attrs = PatientSubmitButtonStyle.toModifier()
                                        .opacity(if (isSubmitting) 0.6f else 1f)
                                        .cursor(if (isSubmitting) Cursor.NotAllowed else Cursor.Pointer).toAttrs {
                                            attr("type", "submit"); if (isSubmitting) attr(
                                            "disabled", "true"
                                        )
                                        }) { Text(if (isSubmitting) data.buttonSubmittingText else data.buttonText) }
                            }

                            if (formSubmitted) {
                                Box(
                                    modifier = Modifier.position(Position.Fixed).top(0.px).left(0.px).fillMaxSize()
                                        .zIndex(1000).backgroundColor(Color.argb(0.6f, 0f, 0f, 0f))
                                        .onClick { formSubmitted = false }, contentAlignment = Alignment.Center
                                ) {
                                    Column(
                                        modifier = Modifier.width(90.percent).maxWidth(500.px)
                                            .backgroundColor(Colors.White).borderRadius(8.px).padding(32.px)
                                            .boxShadow(0.px, 10.px, 30.px, color = Color.argb(0.3f, 0f, 0f, 0f))
                                            .onClick { it.stopPropagation() },
                                        horizontalAlignment = Alignment.CenterHorizontally
                                    ) {
                                        Box(
                                            modifier = Modifier.size(64.px).borderRadius(50.percent)
                                                .backgroundColor(Color.rgb(0xE8F5E9)).margin(bottom = 16.px),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            SpanText(
                                                text = data.modalIconText,
                                                modifier = Modifier.color(Color.rgb(0x2E8B57)).fontSize(32.px)
                                                    .fontWeight(FontWeight.Bold)
                                            )
                                        }
                                        SpanText(
                                            text = data.modalTitle,
                                            modifier = Modifier.fontSize(24.px).fontWeight(FontWeight.Bold)
                                                .color(Color.rgb(0x333333)).margin(bottom = 8.px)
                                        )
                                        SpanText(
                                            text = data.modalDescription,
                                            modifier = Modifier.fontSize(16.px).lineHeight(1.5)
                                                .textAlign(TextAlign.Center).color(Color.rgb(0x666666))
                                                .margin(bottom = 24.px)
                                        )
                                        Button(
                                            attrs = PatientSubmitButtonStyle.toModifier().fontSize(18.px)
                                                .padding(topBottom = 10.px, leftRight = 24.px).toAttrs {
                                                    onClick {
                                                        formSubmitted = false
                                                    }
                                                }) { Text(data.modalCloseButtonText) }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL
// ==========================================
data class PatientSelectOptionData(val value: String, val label: String)

data class ContactPatientFormData(
    val sectionId: String? = null,
    val title: String = "Patient & General Inquiries",
    val description: String = "We are here to support your recovery journey. Whether you are looking for a MyHand<sup>®</sup> equipped clinic near you or need general information, our team is ready to help.",

    // Parameterized Form Copy
    val fullNameLabel: String = "Full Name",
    val fullNamePlaceholder: String = "John Doe",
    val emailLabel: String = "Email Address",
    val emailPlaceholder: String = "john@example.com",
    val locationLabel: String = "City or Postal/Zip Code",
    val locationPlaceholder: String = "To help find a clinic near you",

    val inquiryTypeLabel: String = "How can we help?",
    val inquiryTypePlaceholder: String = "Select an option...",
    val inquiryTypeOptions: List<PatientSelectOptionData> = listOf(
        PatientSelectOptionData("Find Clinic", "I want to find a clinic with the MyHand® System"),
        PatientSelectOptionData("Sign up for a MyHand® System Trial", "Sign up for a MyHand® System Trial"),
        PatientSelectOptionData("General Inquiry", "General Information")
    ),

    val messageLabel: String = "Your Message",
    val messagePlaceholder: String = "Please share any details that might help us assist you better.",

    // ADD THIS NEW PROPERTY:
    val consentText: String = "I consent to IRegained Inc. collecting my contact information to communicate with me regarding my finding a clinic, general information or scheduling a trial of MyHand® System.",

    // UI Feedback Strings
    val buttonText: String = "Send Message",
    val buttonSubmittingText: String = "Sending Message...",
    val alertErrorMessage: String = "Submission connection failed. Please attempt your submission again.",

    // Modal Copy
    val modalIconText: String = "✓",
    val modalTitle: String = "Message Submitted",
    val modalDescription: String = "Thank you for reaching out. We have successfully received your inquiry and are committed to supporting your recovery journey. A team specialist will contact you shortly.",
    val modalCloseButtonText: String = "Close",

    val formProcessingEndpoint: String = "https://script.google.com/a/macros/iregained.rehab/s/AKfycbxGjRZicaotnihoNv7zquaM-Pt2mzYPURWqIT-eeCsqwUow8Hvlw-rq1ih7FNswyrx3/exec"
)

val defaultContactPatientData = ContactPatientFormData()